import { z } from 'zod';
import { DomainError } from '../errors.js';
import { parseLocalDate } from '../local-date.js';
import { toIssues } from '../materials/catalog.js';
import { isValidRunningHours } from '../running-hours.js';
import type { ParseResult } from '../voyage/schemas.js';
import { TEMPLATE_ITEMS, TEMPLATE_SYSTEMS } from './library-template-data.js';

/**
 * Ekipman ağacı ve ekipman kartı (plan M7 Task 1; PMS belgesi §1–§6, §17; ADR 0029). PMS'in
 * temeli ekipmandır: bakım, okuma, ölçüm, arıza ve klas kayıtları ekipmana bağlanır. Saf
 * modül; hata iletileri koddur, arayüz çevirir.
 */

/** Düğüm türü: grup (yalnız kökte), sistem, ekipman, bileşen (PMS belgesi §1, §3). */
export const EQUIPMENT_NODE_TYPES = ['group', 'system', 'equipment', 'component'] as const;
export type EquipmentNodeType = (typeof EQUIPMENT_NODE_TYPES)[number];

/**
 * Ekipmanın durumu; onarım sonrası durumla aynı üç değer [NR496 4.3.2d, 4.5.4d] (K24). "Running"
 * rozeti `operational` demektir.
 */
export const EQUIPMENT_STATUSES = [
  'operational',
  'operational_with_restrictions',
  'not_operational',
] as const;
export type EquipmentStatus = (typeof EQUIPMENT_STATUSES)[number];

/**
 * Klas sörvey prensibi (K31): başmühendis sörveyi ve sörveyör teyidi, sörveyör, durum izleme ya
 * da yok [NR467 Pt A Ch 2 App 2].
 */
export const SURVEY_PRINCIPLES = ['pms_ce', 'surveyor', 'condition_monitoring', 'none'] as const;
export type SurveyPrinciple = (typeof SURVEY_PRINCIPLES)[number];

/**
 * Sayaç türleri: M7'de yalnız çalışma saati (K3; NR496 [4.5.1], UR Z20 1.2.1). Starts, cycles,
 * distance ve load hours ayrı okuma tablosuyla sonra gelir; `has_counter` yalnız çalışma saatini
 * anlatır.
 */
export const COUNTER_KINDS = ['running_hours'] as const;

/** Günlük çalışma saati üst sınırı: günde 24 saatten fazla çalışılamaz [NR496 4.5.1c, 1.4.2]. */
export const MAX_DAILY_HOURS = 24;

/** Ekipman kodu en çok 32 karakter; SFI ya da şirket kodu serbest biçimlidir. */
export const EQUIPMENT_CODE_MAX = 32;

/** Equipment Tag No. (`ME-001`; ADR 0063 §8): en çok 40 karakter, gemide tekil. */
export const EQUIPMENT_TAG_MAX = 40;

/**
 * Önerilen kritiklik: varsayılan iskelette PMS belgesi §17'deki sistemler bu gerekçeyle gelir
 * (K22). Kritikliği şirket belirler [ISM 10.3]; arayüz "Suggested (default structure)" yazar.
 */
export const SUGGESTED_CRITICAL_REASON = 'suggested';

/** Ağaç kuralları için düğümün özü: silinmemiş düğümler. */
export interface EquipmentNode {
  id: string;
  vesselId: string;
  parentId: string | null;
  nodeType: EquipmentNodeType;
  code: string;
  critical: boolean;
  /** Equipment Tag No. (ADR 0063 §8); yoksa `null`. */
  tag?: string | null;
  /** Sayaçlı ekipman: `has_counter` ya da okuması olan ekipman (plan Task 3). */
  hasCounter?: boolean;
}

function byIdOf(tree: readonly EquipmentNode[]): Map<string, EquipmentNode> {
  return new Map(tree.map((n) => [n.id, n]));
}

function nodeOf(byId: ReadonlyMap<string, EquipmentNode>, id: string): EquipmentNode {
  const found = byId.get(id);
  if (!found) throw new DomainError('NOT_FOUND', `equipment ${id}`);
  return found;
}

/** Düğümden köke: düğümün kendisi ve ataları, yakından uzağa. Döngüye karşı korumalı. */
function lineage(byId: ReadonlyMap<string, EquipmentNode>, id: string): EquipmentNode[] {
  const chain: EquipmentNode[] = [];
  const seen = new Set<string>();
  let cursor = byId.get(id);
  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    chain.push(cursor);
    cursor = cursor.parentId === null ? undefined : byId.get(cursor.parentId);
  }
  return chain;
}

/**
 * Düğümün yeni üstü geçerli mi: grup yalnız kökte (`GROUP_NOT_AT_ROOT`), üst aynı gemide
 * (`VESSEL_MISMATCH`), döngü yok (`EQUIPMENT_CYCLE`). `tree` silinmemiş düğümlerdir ve düğümün
 * kendisini (yeni değerleriyle) içerir; ağaçta olmayan üst `NOT_FOUND`.
 */
export function validateEquipmentMove(
  tree: readonly EquipmentNode[],
  id: string,
  parentId: string | null,
): void {
  const byId = byIdOf(tree);
  const self = nodeOf(byId, id);
  if (parentId === null) return;
  if (self.nodeType === 'group') throw new DomainError('GROUP_NOT_AT_ROOT', id);
  if (parentId === id) throw new DomainError('EQUIPMENT_CYCLE', id);
  const parent = nodeOf(byId, parentId);
  if (parent.vesselId !== self.vesselId) {
    throw new DomainError('VESSEL_MISMATCH', `equipment ${parentId}`);
  }
  // Yeni üstten köke yürü: düğümün kendisine rastlanırsa döngü olur
  const seen = new Set<string>();
  let cursor: string | null = parentId;
  while (cursor !== null) {
    if (cursor === id || seen.has(cursor)) throw new DomainError('EQUIPMENT_CYCLE', id);
    seen.add(cursor);
    cursor = byId.get(cursor)?.parentId ?? null;
  }
}

const normalizedCode = (code: string) => code.normalize('NFC').trim().toLocaleLowerCase('en');

/** Kod gemide, silinmemişler arasında tekildir (boşluk ve harf büyüklüğü fark etmez). */
export function assertUniqueEquipmentCode(
  tree: readonly EquipmentNode[],
  node: { id: string; vesselId: string; code: string },
): void {
  const code = normalizedCode(node.code);
  const duplicate = tree.find(
    (other) =>
      other.id !== node.id &&
      other.vesselId === node.vesselId &&
      normalizedCode(other.code) === code,
  );
  if (duplicate) throw new DomainError('DUPLICATE_EQUIPMENT_CODE', node.code);
}

/**
 * Tag gemide silinmemişler arasında tekildir (ADR 0063 §8; boşluk ve harf büyüklüğü fark etmez).
 * Boş tag denetlenmez.
 */
export function assertUniqueEquipmentTag(
  tree: readonly EquipmentNode[],
  node: { id: string; vesselId: string; tag: string | null | undefined },
): void {
  if (node.tag === null || node.tag === undefined || node.tag.trim() === '') return;
  const tag = normalizedCode(node.tag);
  const duplicate = tree.find(
    (other) =>
      other.id !== node.id &&
      other.vesselId === node.vesselId &&
      other.tag !== null &&
      other.tag !== undefined &&
      normalizedCode(other.tag) === tag,
  );
  if (duplicate) throw new DomainError('DUPLICATE_EQUIPMENT_TAG', node.tag);
}

/**
 * Etkin kritiklik: ekipman ya da atalarından biri kritikse kritiktir; bileşenin işi de kritiktir
 * [NR496 4.4; ISM 10.3]. Miras kapatılamaz (K22): alt düğümdeki "kritik değil" işareti etkisizdir.
 */
export function effectiveCritical(tree: readonly EquipmentNode[], id: string): boolean {
  return criticalSourceOf(tree, id) !== undefined;
}

/** Yalnız kendisi kritik işaretli düğüm (K22: "No test job" uyarısı yalnız burada hesaplanır). */
export function explicitlyCritical(tree: readonly EquipmentNode[], id: string): boolean {
  return byIdOf(tree).get(id)?.critical === true;
}

/** Kritikliğin kaynağı: kendisi ya da en yakın kritik ata; kritik değilse `undefined`. */
export function criticalSourceOf(tree: readonly EquipmentNode[], id: string): string | undefined {
  return lineage(byIdOf(tree), id).find((n) => n.critical)?.id;
}

/**
 * Ekipman silme kuralı (K26; `DELETE_GUARDS`'ın kullandığı saf kural): alt düğümü olan ekipman
 * silinemez, silme gerekçe ister. Geçmiş görünür kalır [NR496 4.3.2a, 4.6.3e]. Etkin iş ve açık
 * iş emri kontrolü M7b'de eklenir.
 */
export function assertDecommission(
  tree: readonly EquipmentNode[],
  id: string,
  reason: string | null | undefined,
): void {
  if (tree.some((n) => n.parentId === id)) throw new DomainError('EQUIPMENT_HAS_CHILDREN', id);
  if (!reason?.trim()) throw new DomainError('DECOMMISSION_REASON_REQUIRED', id);
}

/* ---------- Ekipman kartı (PMS belgesi §4–§5) ---------- */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');

const text = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));

const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

/** Çok satırlı not: satır sonları korunur, boş satırlar atılır. */
const optionalLines = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) =>
      value
        .normalize('NFC')
        .split(/\r\n|\r|\n/)
        .map(collapse)
        .filter((line) => line !== '')
        .join('\n'),
    )
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

const flag = () =>
  z
    .boolean(typed('INVALID_OPTION'))
    .nullish()
    .transform((value) => value ?? false);

const option = <T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values, typed('INVALID_OPTION'));

const number = (min: number, max: number) =>
  z
    .number(typed('INVALID_NUMBER'))
    .min(min, 'OUT_OF_RANGE')
    .max(max, 'OUT_OF_RANGE')
    .nullish()
    .transform((value) => value ?? null);

const integer = (min: number, max: number) =>
  z
    .number(typed('INVALID_NUMBER'))
    .int('INVALID_NUMBER')
    .min(min, 'OUT_OF_RANGE')
    .max(max, 'OUT_OF_RANGE')
    .nullish()
    .transform((value) => value ?? null);

const localDate = z.string(typed('INVALID_DATE')).refine((value) => {
  try {
    parseLocalDate(value);
    return true;
  } catch {
    return false;
  }
}, 'INVALID_DATE');

/** Noon Report eşlemesi (K2): ana makine ya da adı verilen yardımcı makine. */
const NOON_SOURCE = /^(main_engine|auxiliary:\S.{0,59})$/;

/** Teknik bilgiler (PMS belgesi §5); hepsi isteğe bağlıdır. */
export const equipmentTechnicalSchema = z.strictObject({
  powerKw: number(0, 200_000),
  rpm: number(0, 20_000),
  fuelType: optionalText(40),
  cylinders: integer(1, 40),
  coolingType: optionalText(40),
  lubricationType: optionalText(40),
  startingSystem: optionalText(40),
});
export type EquipmentTechnical = z.output<typeof equipmentTechnicalSchema>;

/**
 * Ekipman kartı (PMS belgesi §4–§5; plan Task 1). Tanımsız alan kabul edilmez: başka sayaç türü
 * (starts, cycles) eklenemez (K3).
 */
export const equipmentInputSchema = z
  .strictObject({
    nodeType: option(EQUIPMENT_NODE_TYPES),
    code: text(EQUIPMENT_CODE_MAX),
    name: text(120),
    tag: optionalText(EQUIPMENT_TAG_MAX),
    sfiCode: optionalText(32),
    maker: optionalText(120),
    model: optionalText(120),
    serialNo: optionalText(80),
    installedOn: localDate.nullish().transform((value) => value ?? null),
    location: optionalText(120),
    status: option(EQUIPMENT_STATUSES)
      .nullish()
      .transform((value) => value ?? 'operational'),
    critical: flag(),
    criticalReason: optionalText(500),
    classRelevant: flag(),
    ismRelevant: flag(),
    safetyCritical: flag(),
    outOfPmsManual: flag(),
    outOfPmsReason: optionalText(500),
    bvItemCode: optionalText(32),
    surveyPrinciple: option(SURVEY_PRINCIPLES)
      .nullish()
      .transform((value) => value ?? null),
    hasCounter: flag(),
    maxDailyHours: z
      .number(typed('INVALID_NUMBER'))
      .nullish()
      .transform((value) => value ?? MAX_DAILY_HOURS),
    noonReportSource: z
      .string(typed('INVALID_NOON_SOURCE'))
      .transform((value) => value.trim())
      .nullish()
      .transform((value) => (value ? value : null)),
    technical: equipmentTechnicalSchema.nullish().transform((value) => value ?? null),
    notes: optionalLines(2000),
  })
  .superRefine((input, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', message, path: [path] });
    // Elle "PMS dışı" işareti gerekçeli (K25) [UR Z20 3.3.3]
    if (input.outOfPmsManual && input.outOfPmsReason === null) {
      issue('outOfPmsReason', 'OUT_OF_PMS_REASON_REQUIRED');
    }
    // Ayar 24'ü aşamaz; aşarsa [NR496 4.5.1c] kontrolü kapanmış olur [NR496 1.4.2] (K5)
    if (input.maxDailyHours > MAX_DAILY_HOURS) issue('maxDailyHours', 'MAX_DAILY_OVER_24');
    else if (input.maxDailyHours < 1 || !isValidRunningHours(input.maxDailyHours)) {
      issue('maxDailyHours', 'OUT_OF_RANGE');
    }
    if (input.noonReportSource !== null) {
      if (!NOON_SOURCE.test(input.noonReportSource)) {
        issue('noonReportSource', 'INVALID_NOON_SOURCE');
      } else if (!input.hasCounter) {
        issue('noonReportSource', 'NOON_SOURCE_REQUIRES_COUNTER');
      }
    }
  });
export type EquipmentInput = z.output<typeof equipmentInputSchema>;

/**
 * Ekipman kartını ayrıştırır. `today` gemi sunucusunun yerel günüdür: kurulum tarihi gelecekte
 * olamaz (CLAUDE.md "Zaman").
 */
export function parseEquipmentInput(
  input: unknown,
  options: { today?: string } = {},
): ParseResult<EquipmentInput> {
  const result = equipmentInputSchema.safeParse(input);
  if (!result.success) return { ok: false, issues: toIssues(result.error) };
  const { installedOn } = result.data;
  if (options.today !== undefined && installedOn !== null && installedOn > options.today) {
    return { ok: false, issues: [{ path: 'installedOn', code: 'FUTURE_DATE' }] };
  }
  return { ok: true, value: result.data };
}

/* ---------- Varsayılan iskelet (PMS belgesi §1, §17; K22) ---------- */

export interface SkeletonNode {
  /** İskelet içindeki anahtar; `parent` buna bağlanır. */
  key: string;
  parent: string | null;
  nodeType: EquipmentNodeType;
  code: string;
  name: string;
  critical: boolean;
  criticalReason: string | null;
}

/**
 * Varsayılan iskeletin önerilen kritik düğümleri (PMS belgesi §17; K22): ortağın kütüphanesindeki
 * kalem kodlarıyla (ADR 0063 §9). Ad kalemin alt sistemidir; kütüphane uygulanınca düğüm koddan
 * bağlanır, kopya oluşmaz.
 */
export const SKELETON_CRITICAL_ITEMS: readonly { code: string; system: string }[] = [
  { code: '01-001', system: '01' },
  { code: '02-001', system: '02' },
  { code: '02-002', system: '02' },
  { code: '08-014', system: '08' },
  { code: '09-001', system: '09' },
  { code: '10-001', system: '10' },
  { code: '16-001', system: '16' },
  { code: '16-002', system: '16' },
  { code: '16-004', system: '16' },
];

/** Gemi tipine özel sistem (DP, offshore, yat, balıkçı…): iskelette yok, kütüphaneyle gelir. */
const VESSEL_TYPE_SPECIFIC_SYSTEMS = new Set(['22']);

/**
 * Varsayılan ekipman iskeleti (ADR 0063 §9): ortağın kütüphanesinin 01–21 sistemleri kök grup
 * (aynı kod ve ad) ve §17'deki dokuz sistem "önerilen" kritik ekipman olarak (K22); kritikliği
 * şirket belirler [ISM 10.3]. Yalnız açık kullanıcı eylemiyle ve o kişinin adına, ekipmanı
 * olmayan gemiye yüklenir (plan Task 4); var olan gemilerin ağacı değişmez.
 */
export const DEFAULT_EQUIPMENT_SKELETON: readonly SkeletonNode[] = [
  ...TEMPLATE_SYSTEMS.filter(([code]) => !VESSEL_TYPE_SPECIFIC_SYSTEMS.has(code)).map(
    ([code, name]): SkeletonNode => ({
      key: code,
      parent: null,
      nodeType: 'group',
      code,
      name,
      critical: false,
      criticalReason: null,
    }),
  ),
  ...SKELETON_CRITICAL_ITEMS.map(({ code, system }): SkeletonNode => {
    const item = TEMPLATE_ITEMS.find(([itemCode]) => itemCode === code);
    if (!item) throw new Error(`iskelet kalemi şablonda yok: ${code}`);
    return {
      key: code,
      parent: system,
      nodeType: 'equipment',
      code,
      name: item[2],
      critical: true,
      criticalReason: SUGGESTED_CRITICAL_REASON,
    };
  }),
];
